<template>
  <div class="referrals-page">
    <PageHeader title="Programa de Referidos" />

    <div class="referrals-content">
      <!-- Christmas Banner -->
      <div class="christmas-banner card">
        <div class="banner-icon">🎄</div>
        <h2 class="banner-title">¡Comparte Eurekers por Navidad!</h2>
        <p class="banner-text">
          Regala conocimiento financiero a tus seres queridos. <br><br>Válido hasta el 7 de enero de 2026.
        </p>
      </div>

      <!-- Referral Code Section -->
      <div class="referral-code-section card">
        <h3 class="section-title">Tu código de referido</h3>
        
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Cargando...</p>
        </div>

        <div v-else-if="error" class="error-state">
          <p class="error-message">{{ error }}</p>
          <button @click="loadReferralData" class="btn btn-primary">Reintentar</button>
        </div>

        <div v-else class="code-display">
          <div class="code-box">
            <span class="code-text">{{ referralCode }}</span>
            <button @click="copyCode" class="btn-copy" :class="{ copied: codeCopied }">
              <span v-if="!codeCopied">📋 Copiar</span>
              <span v-else>✓ Copiado</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Rewards Section -->
      <div class="rewards-section card">
        <h3 class="section-title">¿Cómo funciona?</h3>
        <div class="rewards-content">
          <div class="reward-step">
            <div class="step-number">1</div>
            <div class="step-content">
              <h4>Comparte tu código</h4>
              <p>Envía tu código de referido a amigos, familiares o conocidos.</p>
            </div>
          </div>
          
          <div class="reward-step">
            <div class="step-number">2</div>
            <div class="step-content">
              <h4>Ellos activan el código en su cuenta</h4>
              <p>Tus referidos deben introducir el código en el siguiente link, y el precio de compra del curso básico de Eurekersse reducirá en un 20%</p>
              <div class="referral-url-box">
                <p class="referral-url"><strong>https://online.eurekers.com/referral</strong></p>
                <button @click="copyReferralUrl" class="btn-copy-small" :class="{ copied: referralUrlCopied }">
                  <span v-if="!referralUrlCopied">📋</span>
                  <span v-else>✓</span>
                </button>
              </div>
              <p class="note-text">Nota: Tu contador no se actualizará hasta que compren el curso.</p>
            </div>
          </div>
          
          <div class="reward-step">
            <div class="step-number">3</div>
            <div class="step-content">
              <h4>Obtén recompensas</h4>
              <p class="highlight">
                <strong>¡A partir del segundo usuario que compre con tu código, 
                recibirás 1 mes de Platinum por cada nuevo referido!</strong>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Statistics Section -->
      <div class="stats-section card">
        <h3 class="section-title">Tus estadísticas</h3>
        
        <div v-if="statsLoading" class="loading-state">
          <div class="spinner"></div>
        </div>

        <div v-else class="stats-content">
          <div class="stat-card">
            <div class="stat-number">{{ totalUses }}</div>
            <div class="stat-label">Usuarios que han usado tu código</div>
          </div>

          <div v-if="totalUses > 0" class="stat-card">
            <div class="stat-number">{{ rewardMonths }}</div>
            <div class="stat-label">Meses de Platinum ganados</div>
          </div>

          <div v-if="totalUses === 0" class="no-uses-message">
            <p>Aún no has tenido referidos. ¡Comparte tu código y empieza a ganar recompensas! 🚀</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import PageHeader from '@/shared/components/common/PageHeader.vue';

export default {
  name: 'Referrals',
  components: {
    PageHeader
  },
  data() {
    return {
      loading: true,
      statsLoading: true,
      error: null,
      referralCode: '',
      codeCopied: false,
      urlCopied: false,
      referralUrlCopied: false,
      totalUses: 0
    };
  },
  computed: {
    shareUrl() {
      return `${window.location.origin}/app/user-register?ref=${this.referralCode}`;
    },
    rewardMonths() {
      // A partir del segundo usuario, cada uno da 1 mes
      return Math.max(0, this.totalUses - 1);
    }
  },
  mounted() {
    this.checkSession();
    this.loadReferralData();
  },
  methods: {
    checkSession() {
      const uid = localStorage.getItem('uid');
      if (!uid || uid === '0') {
        this.$router.push('/app/login');
      }
    },
    async loadReferralData() {
      this.loading = true;
      this.error = null;
      
      try {
        // Cargar código de referido
        const codeResponse = await axios.get(`${process.env.VUE_APP_API}/api/v1/referrals/my-code`);
        
        if (codeResponse.data.status === 'success') {
          this.referralCode = codeResponse.data.code;
        } else {
          throw new Error(codeResponse.data.message || 'Error al obtener el código');
        }

        // Cargar estadísticas
        await this.loadStats();
      } catch (err) {
        console.error('Error loading referral data:', err);
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          this.$router.push('/app/login');
          return;
        }
        this.error = 'No se pudo cargar tu código de referido. Por favor, inténtalo de nuevo.';
      } finally {
        this.loading = false;
      }
    },

    async loadStats() {
      this.statsLoading = true;
      
      try {
        const statsResponse = await axios.get(`${process.env.VUE_APP_API}/api/v1/referrals/my-stats`);
        
        if (statsResponse.data.status === 'success') {
          this.totalUses = statsResponse.data.total_uses;
        }
      } catch (err) {
        console.error('Error loading stats:', err);
      } finally {
        this.statsLoading = false;
      }
    },

    copyCode() {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.referralCode).then(() => {
          this.codeCopied = true;
          setTimeout(() => {
            this.codeCopied = false;
          }, 2000);
        });
      } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = this.referralCode;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        this.codeCopied = true;
        setTimeout(() => {
          this.codeCopied = false;
        }, 2000);
      }
    },

    copyUrl() {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.shareUrl).then(() => {
          this.urlCopied = true;
          setTimeout(() => {
            this.urlCopied = false;
          }, 2000);
        });
      } else {
        const input = this.$refs.urlInput;
        input.select();
        document.execCommand('copy');
        this.urlCopied = true;
        setTimeout(() => {
          this.urlCopied = false;
        }, 2000);
      }
    },

    copyReferralUrl() {
      const referralUrl = 'https://online.eurekers.com/referral';
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(referralUrl).then(() => {
          this.referralUrlCopied = true;
          setTimeout(() => {
            this.referralUrlCopied = false;
          }, 2000);
        });
      } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = referralUrl;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        this.referralUrlCopied = true;
        setTimeout(() => {
          this.referralUrlCopied = false;
        }, 2000);
      }
    },

  }
};
</script>

<style scoped>
.referrals-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background: var(--color-background);
}

.referrals-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.card {
  background: var(--color-background);
  border-radius: 12px;
  padding: 28px;
  border: 1px solid var(--color-outline);
}

/* Christmas Banner */
.christmas-banner {
  background: linear-gradient(135deg, #c41e3a 0%, #165b33 100%);
  color: white;
  text-align: center;
  padding: 32px;
}

.banner-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.banner-title {
  font-size: 28px;
  font-weight: bold;
  margin: 0 0 12px 0;
}

.banner-text {
  font-size: 16px;
  opacity: 0.95;
  margin: 0;
  max-width: 600px;
  margin: 0 auto;
}

/* Section Title */
.section-title {
  font-size: 22px;
  font-weight: bold;
  margin: 0 0 20px 0;
  color: var(--color-text-primary);
}

/* Loading and Error States */
.loading-state {
  text-align: center;
  padding: 40px 20px;
  color: var(--color-text-secondary);
}

.spinner {
  border: 3px solid var(--color-surface);
  border-top: 3px solid var(--color-accent-primary);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-state {
  text-align: center;
  padding: 40px 20px;
}

.error-message {
  color: var(--color-error);
  margin-bottom: 16px;
}

/* Code Display */
.code-display {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.code-box {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: var(--color-surface);
  border-radius: 8px;
  border: 2px dashed var(--color-accent-primary);
}

.code-text {
  flex: 1;
  font-size: 32px;
  font-weight: bold;
  font-family: 'Courier New', monospace;
  color: var(--color-text-primary);
  letter-spacing: 2px;
}

.btn-copy {
  padding: 12px 24px;
  background: var(--color-accent-primary);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-copy:hover {
  background: var(--color-accent-secondary);
  transform: translateY(-2px);
}

.btn-copy.copied {
  background: var(--color-success);
}

/* Share URL */
.share-url {
  display: flex;
  gap: 12px;
}

.url-input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid var(--color-outline);
  border-radius: 6px;
  font-size: 14px;
  background: var(--color-input-background);
  color: var(--color-text-primary);
}

.btn-copy-url {
  padding: 12px 24px;
  background: var(--color-success);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.btn-copy-url:hover {
  background: var(--color-accent-secondary);
}

.btn-copy-url.copied {
  background: var(--color-accent-secondary);
}

/* Rewards Section */
.rewards-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.reward-step {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.step-number {
  width: 48px;
  height: 48px;
  background: var(--color-accent-primary);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  flex-shrink: 0;
}

.step-content h4 {
  margin: 0 0 8px 0;
  font-size: 18px;
  color: var(--color-text-primary);
}

.step-content p {
  margin: 0;
  color: var(--color-text-secondary);
  line-height: 1.5;
}

.referral-url-box {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--color-surface);
  padding: 12px 16px;
  border-radius: 6px;
  border-left: 4px solid var(--color-accent-primary);
  margin: 12px 0;
}

.referral-url {
  flex: 1;
  font-size: 18px;
  text-align: center;
  margin: 0 !important;
}

.referral-url strong {
  color: var(--color-accent-primary);
  font-family: 'Courier New', monospace;
  letter-spacing: 0.5px;
}

.btn-copy-small {
  padding: 8px 16px;
  background: var(--color-accent-primary);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.btn-copy-small:hover {
  background: var(--color-accent-secondary);
  transform: translateY(-2px);
}

.btn-copy-small.copied {
  background: var(--color-success);
}

.note-text {
  font-size: 14px;
  font-style: italic;
  color: var(--color-text-tertiary);
  margin-top: 8px !important;
}

.highlight {
  background: var(--color-active-surface);
  padding: 12px 16px;
  border-radius: 6px;
  border-left: 4px solid var(--color-warning);
  margin-top: 8px;
}

.highlight strong {
  color: var(--color-text-primary);
}

/* Statistics Section */
.stats-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.stat-card {
  text-align: center;
  padding: 24px;
  background: linear-gradient(135deg, var(--color-accent-600) 0%, var(--color-accent-800) 100%);
  color: white;
  border-radius: 12px;
}

.stat-number {
  font-size: 48px;
  font-weight: bold;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 16px;
  opacity: 0.9;
}

.no-uses-message {
  text-align: center;
  padding: 32px;
  background: var(--color-surface);
  border-radius: 8px;
}

.no-uses-message p {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 16px;
}

.btn-primary {
  padding: 12px 24px;
  background: var(--color-accent-primary);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-primary:hover {
  background: var(--color-accent-secondary);
}

/* Responsive */
@media (max-width: 768px) {
  .referrals-page {
    padding: 12px;
  }

  .card {
    padding: 20px;
  }

  .code-text {
    font-size: 24px;
  }

  .banner-title {
    font-size: 24px;
  }

  .reward-step {
    flex-direction: column;
    gap: 12px;
  }

  .share-url {
    flex-direction: column;
  }

  .referral-url-box {
    flex-direction: column;
    gap: 8px;
  }

  .btn-copy-small {
    width: 100%;
  }
}
</style>

